import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar, Ship, Users, Euro, TrendingUp, ChevronLeft, ChevronRight,
  Anchor, CheckCircle2, Clock, Plus, Edit, Trash2, User, X, Eye, EyeOff, Save, Lock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import PreBoardingSection from './PreBoardingSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

// ─── Passenger Row ───
const PassengerRow = ({ p, depId, token, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...p });
  const totalDue = (p.price_per_person || 0) + (p.caisse_de_bord || 0);
  const remaining = totalDue - (p.amount_paid || 0);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/departures/${depId}/passengers/${p.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Passager mis a jour');
      setEditing(false);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };
  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    try {
      await axios.delete(`${API}/departures/${depId}/passengers/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Passager supprime');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  if (editing) return (
    <div className="bg-white border border-[#D4AF37]/30 rounded-lg p-3 space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><Label className="text-[10px]">Nom *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px]">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px]">Telephone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px]">Cabine n</Label><Input type="number" min="1" max="4" value={form.cabin_number} onChange={e => setForm(f => ({ ...f, cabin_number: parseInt(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><Label className="text-[10px] text-emerald-700 font-semibold">Prix / passager (EUR)</Label><Input type="number" value={form.price_per_person || 0} onChange={e => setForm(f => ({ ...f, price_per_person: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-emerald-300" /></div>
        <div><Label className="text-[10px] text-emerald-700 font-semibold">Caisse de bord (EUR)</Label><Input type="number" value={form.caisse_de_bord || 0} onChange={e => setForm(f => ({ ...f, caisse_de_bord: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-emerald-300" /></div>
        <div><Label className="text-[10px] text-blue-700 font-semibold">Montant encaisse (EUR)</Label><Input type="number" value={form.amount_paid || 0} onChange={e => setForm(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-blue-300" /></div>
        <div>
          <Label className="text-[10px] text-[#94A3B8]">Reste a encaisser</Label>
          <div className={`h-8 flex items-center px-2 text-sm font-bold rounded-md ${((form.price_per_person || 0) + (form.caisse_de_bord || 0) - (form.amount_paid || 0)) > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {((form.price_per_person || 0) + (form.caisse_de_bord || 0) - (form.amount_paid || 0)).toLocaleString('fr-FR')} EUR
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px]">Allergies</Label><Input value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))} className="h-8 text-sm" placeholder="Ex: gluten, lactose..." /></div>
        <div><Label className="text-[10px]">Contraintes medicales</Label><Input value={form.medical} onChange={e => setForm(f => ({ ...f, medical: e.target.value }))} className="h-8 text-sm" placeholder="Ex: mal de mer..." /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px]">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="text-sm min-h-[40px]" /></div>
        <div><Label className="text-[10px]">Notes paiement</Label><Textarea value={form.payment_notes || ''} onChange={e => setForm(f => ({ ...f, payment_notes: e.target.value }))} className="text-sm min-h-[40px]" placeholder="CB, virement, cheque..." /></div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-[#2A9D8F] text-white h-7 text-xs" onClick={handleSave}>Enregistrer</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>Annuler</Button>
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-[#E2E8F0] hover:border-[#D4AF37]/30 transition-colors" data-testid={`passenger-${p.id}`}>
      <div className="flex items-center gap-3 text-sm min-w-0 flex-1">
        <div className="w-7 h-7 bg-[#0A2540] rounded-full flex items-center justify-center shrink-0">
          <span className="text-white text-xs font-bold">{p.cabin_number || '?'}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#0A2540]">{p.name}</span>
            {p.email && <span className="text-[#94A3B8] text-xs hidden sm:inline">{p.email}</span>}
            {p.allergies && <Badge className="bg-red-50 text-red-600 text-[10px] hidden sm:inline-flex">Allergies: {p.allergies}</Badge>}
          </div>
          {totalDue > 0 && (
            <div className="flex items-center gap-3 text-[10px] mt-0.5">
              <span className="text-[#4A5568]">Total: <b>{totalDue.toLocaleString('fr-FR')} EUR</b></span>
              <span className="text-blue-600">Paye: <b>{(p.amount_paid || 0).toLocaleString('fr-FR')} EUR</b></span>
              {remaining > 0 ? (
                <span className="text-red-600 font-bold">Reste: {remaining.toLocaleString('fr-FR')} EUR</span>
              ) : (
                <span className="text-emerald-600 font-bold">Solde OK</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setForm({ ...p }); setEditing(true); }}><Edit className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={handleDelete}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
};

// ─── Add Passenger Form ───
const AddPassengerForm = ({ depId, cabinPrice, token, onAdded, onCancel }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', cabin_number: 1, allergies: '', medical: '', notes: '', price_per_person: cabinPrice || 0, caisse_de_bord: 240, amount_paid: 0, payment_notes: '' });
  const [saving, setSaving] = useState(false);
  const totalDue = (form.price_per_person || 0) + (form.caisse_de_bord || 0);
  const remaining = totalDue - (form.amount_paid || 0);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/departures/${depId}/passengers`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${form.name} ajoute`);
      setForm({ name: '', email: '', phone: '', cabin_number: 1, allergies: '', medical: '', notes: '', price_per_person: cabinPrice || 0, caisse_de_bord: 240, amount_paid: 0, payment_notes: '' });
      onAdded();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#D4AF37]/5 border-2 border-dashed border-[#D4AF37]/30 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="font-semibold text-xs text-[#0A2540]">Ajouter un passager</h5>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onCancel}><X className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><Label className="text-[10px]">Nom complet *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" placeholder="Jean Dupont" /></div>
        <div><Label className="text-[10px]">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px]">Telephone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px]">Cabine n</Label><Input type="number" min="1" max="4" value={form.cabin_number} onChange={e => setForm(f => ({ ...f, cabin_number: parseInt(e.target.value) || 1 }))} className="h-8 text-sm" /></div>
      </div>
      {/* Financial fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 bg-emerald-50/50 rounded-lg border border-emerald-200">
        <div><Label className="text-[10px] text-emerald-700 font-semibold">Prix / passager (EUR)</Label><Input type="number" value={form.price_per_person} onChange={e => setForm(f => ({ ...f, price_per_person: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-emerald-300" /></div>
        <div><Label className="text-[10px] text-emerald-700 font-semibold">Caisse de bord (EUR)</Label><Input type="number" value={form.caisse_de_bord} onChange={e => setForm(f => ({ ...f, caisse_de_bord: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-emerald-300" /></div>
        <div><Label className="text-[10px] text-blue-700 font-semibold">Montant encaisse (EUR)</Label><Input type="number" value={form.amount_paid} onChange={e => setForm(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm border-blue-300" /></div>
        <div>
          <Label className="text-[10px] text-[#94A3B8]">Reste a encaisser</Label>
          <div className={`h-8 flex items-center px-2 text-sm font-bold rounded-md ${remaining > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {remaining.toLocaleString('fr-FR')} EUR
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px]">Allergies</Label><Input value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))} className="h-8 text-sm" placeholder="Gluten, lactose..." /></div>
        <div><Label className="text-[10px]">Contraintes medicales</Label><Input value={form.medical} onChange={e => setForm(f => ({ ...f, medical: e.target.value }))} className="h-8 text-sm" placeholder="Mal de mer..." /></div>
      </div>
      <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-7 text-xs" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Ajout...' : 'Ajouter le passager'}
      </Button>
    </div>
  );
};

// ─── Edit Departure Form ───
const EditDepartureForm = ({ dep, cruises, token, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    start_date: dep.start_date || '',
    end_date: dep.end_date || '',
    departure_time: dep.departure_time || '',
    arrival_time: dep.arrival_time || '',
    client_name: dep.client_name || '',
    cabin_price: dep.cabin_price || 0,
    privatization_price: dep.privatization_price || 0,
    available_cabins: dep.available_cabins ?? 4,
    status: dep.status || 'available',
    is_public: dep.is_public !== false,
    notes: dep.notes || '',
    cruise_id: dep.cruise_id || '',
    cruise_slug: dep.cruise_slug || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cruise-dates/${dep.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Depart mis a jour');
      onSaved();
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#F0F7FF] border border-blue-200 rounded-lg p-4 space-y-3" data-testid="edit-departure-form">
      <div className="flex items-center justify-between">
        <h5 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2">
          <Edit className="h-4 w-4" /> Modifier le depart
        </h5>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onCancel}><X className="h-3.5 w-3.5" /></Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-[10px] font-medium">Date depart</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px] font-medium">Date retour</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px] font-medium">Heure embarquement</Label><Input type="time" value={form.departure_time} onChange={e => setForm(f => ({ ...f, departure_time: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px] font-medium">Heure debarquement</Label><Input type="time" value={form.arrival_time} onChange={e => setForm(f => ({ ...f, arrival_time: e.target.value }))} className="h-8 text-sm" /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-[10px] font-medium">Prix cabine (EUR)</Label><Input type="number" value={form.cabin_price} onChange={e => setForm(f => ({ ...f, cabin_price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px] font-medium">Prix privatisation (EUR)</Label><Input type="number" value={form.privatization_price} onChange={e => setForm(f => ({ ...f, privatization_price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
        <div><Label className="text-[10px] font-medium">Cabines disponibles</Label><Input type="number" min="0" max="4" value={form.available_cabins} onChange={e => setForm(f => ({ ...f, available_cabins: parseInt(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
        <div>
          <Label className="text-[10px] font-medium">Statut</Label>
          <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Disponible</SelectItem>
              <SelectItem value="few_spots">Quelques places</SelectItem>
              <SelectItem value="full">Complet</SelectItem>
              <SelectItem value="cancelled">Annule</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] font-medium">Visibilite</Label>
          <div className="flex items-center gap-3 mt-1">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, is_public: true }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${form.is_public ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            >
              <Eye className="h-3.5 w-3.5" /> Public (visible sur le site)
            </button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, is_public: false }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!form.is_public ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            >
              <Lock className="h-3.5 w-3.5" /> Prive (admin uniquement)
            </button>
          </div>
        </div>
      </div>

      <div><Label className="text-[10px] font-medium text-[#D4AF37]">Nom du client</Label><Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-8 text-sm border-[#D4AF37]/30 bg-[#D4AF37]/5" placeholder="Ex: Famille Dupont, M. Rossi..." /></div>

      <div><Label className="text-[10px] font-medium">Notes internes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="text-sm min-h-[40px]" placeholder="Notes reservees a l'admin..." /></div>

      <div className="flex gap-2">
        <Button size="sm" className="bg-[#2A9D8F] text-white h-8 text-xs" onClick={handleSave} disabled={saving}>
          <Save className="h-3.5 w-3.5 mr-1" />{saving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
};

// ─── New Private Departure Form ───
const NewDepartureForm = ({ cruises, token, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    cruise_id: '', cruise_slug: '', start_date: '', end_date: '',
    departure_time: '', arrival_time: '', client_name: '',
    cabin_price: 0, privatization_price: 0, available_cabins: 4,
    catamaran: 'LAGOON 43', is_public: false, notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleCruiseChange = (slug) => {
    const cruise = cruises.find(c => c.slug === slug);
    if (cruise) {
      setForm(f => ({
        ...f, cruise_id: cruise.id, cruise_slug: cruise.slug,
        cabin_price: cruise.base_price || 0,
        privatization_price: cruise.privatization_base_price || 0,
      }));
    }
  };

  const handleCreate = async () => {
    if (!form.cruise_id || !form.start_date || !form.end_date) {
      toast.error('Croisiere, date depart et retour requis');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/cruise-dates`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Nouveau depart cree');
      onCreated();
    } catch { toast.error('Erreur de creation'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50 shadow-lg" data-testid="new-departure-form">
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nouveau depart {!form.is_public && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Lock className="h-2.5 w-2.5 mr-0.5" />Prive</Badge>}
          </h4>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onCancel}><X className="h-4 w-4" /></Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[10px] font-medium">Croisiere *</Label>
            <Select value={form.cruise_slug} onValueChange={handleCruiseChange}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Choisir une croisiere..." /></SelectTrigger>
              <SelectContent>
                {cruises.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] font-medium">Date depart *</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="h-8 text-sm" /></div>
          <div><Label className="text-[10px] font-medium">Date retour *</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="h-8 text-sm" /></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-[10px] font-medium">Heure embarquement</Label><Input type="time" value={form.departure_time} onChange={e => setForm(f => ({ ...f, departure_time: e.target.value }))} className="h-8 text-sm" placeholder="Ex: 17:00" /></div>
          <div><Label className="text-[10px] font-medium">Heure debarquement</Label><Input type="time" value={form.arrival_time} onChange={e => setForm(f => ({ ...f, arrival_time: e.target.value }))} className="h-8 text-sm" placeholder="Ex: 09:00" /></div>
          <div><Label className="text-[10px] font-medium">Prix cabine (EUR)</Label><Input type="number" value={form.cabin_price} onChange={e => setForm(f => ({ ...f, cabin_price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
          <div><Label className="text-[10px] font-medium">Prix privatisation (EUR)</Label><Input type="number" value={form.privatization_price} onChange={e => setForm(f => ({ ...f, privatization_price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
          <div><Label className="text-[10px] font-medium">Cabines</Label><Input type="number" min="0" max="4" value={form.available_cabins} onChange={e => setForm(f => ({ ...f, available_cabins: parseInt(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
          <div>
            <Label className="text-[10px] font-medium">Visibilite</Label>
            <div className="flex items-center gap-2 mt-1">
              <button type="button" onClick={() => setForm(f => ({ ...f, is_public: false }))}
                className={`flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium transition-all ${!form.is_public ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'bg-gray-100 text-gray-500'}`}>
                <Lock className="h-3 w-3" /> Prive
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, is_public: true }))}
                className={`flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium transition-all ${form.is_public ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300' : 'bg-gray-100 text-gray-500'}`}>
                <Eye className="h-3 w-3" /> Public
              </button>
            </div>
          </div>
        </div>

        <div><Label className="text-[10px] font-medium">Notes internes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" placeholder="Notes pour cet embarquement..." /></div>

        <div><Label className="text-[10px] font-medium text-[#D4AF37]">Nom du client</Label><Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-8 text-sm border-[#D4AF37]/30 bg-[#D4AF37]/5" placeholder="Ex: Famille Dupont, M. Rossi..." /></div>

        <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-8 text-xs" onClick={handleCreate} disabled={saving}>
          {saving ? 'Creation...' : 'Creer le depart'}
        </Button>
      </CardContent>
    </Card>
  );
};


// ─── Main Planning Component ───
const AdminPlanning = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterCruise, setFilterCruise] = useState('all');
  const [selectedDep, setSelectedDep] = useState(null);
  const [showAddPassenger, setShowAddPassenger] = useState(false);
  const [showEditDep, setShowEditDep] = useState(false);
  const [showNewDep, setShowNewDep] = useState(false);
  const { token } = useAuth();

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/planning/overview`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
      if (selectedDep) {
        const updated = res.data.departures.find(d => d.id === selectedDep.id);
        if (updated) setSelectedDep(updated);
        else setSelectedDep(null);
      }
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const departures = useMemo(() => {
    if (!data) return [];
    return data.departures.filter(d => filterCruise === 'all' || d.cruise_slug === filterCruise);
  }, [data, filterCruise]);

  const monthDepartures = useMemo(() => {
    const monthStart = new Date(selectedYear, selectedMonth, 1);
    const monthEnd = new Date(selectedYear, selectedMonth + 1, 0);
    return departures.filter(d => {
      const start = new Date(d.start_date + 'T00:00:00');
      const end = new Date(d.end_date + 'T00:00:00');
      return start <= monthEnd && end >= monthStart;
    });
  }, [departures, selectedMonth, selectedYear]);

  const stats = useMemo(() => {
    const upcoming = departures.filter(d => new Date(d.start_date) >= new Date());
    const totalCabins = upcoming.reduce((s, d) => s + (d.total_cabins || 4), 0);
    const bookedCabins = upcoming.reduce((s, d) => s + (d.booked_cabins || 0), 0);
    const confirmedRev = upcoming.reduce((s, d) => s + (d.confirmed_revenue || 0), 0);
    const pendingRev = upcoming.reduce((s, d) => s + (d.pending_revenue || 0), 0);
    const totalPassengers = upcoming.reduce((s, d) => s + (d.passengers?.length || 0), 0);
    return {
      totalDepartures: upcoming.length, avgOccupancy: totalCabins > 0 ? Math.round(bookedCabins / totalCabins * 100) : 0,
      confirmedRevenue: confirmedRev, pendingRevenue: pendingRev,
      bookedCabins, totalCabins, totalPassengers,
    };
  }, [departures]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  }, [selectedMonth, selectedYear]);

  // Check if a day falls within a departure's date range
  const getDepsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayDate = new Date(dateStr + 'T00:00:00');
    return departures.filter(d => {
      const start = new Date(d.start_date + 'T00:00:00');
      const end = new Date(d.end_date + 'T00:00:00');
      return dayDate >= start && dayDate <= end;
    });
  };

  // Position of day within the departure range
  const getDayPosition = (day, dep) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (dateStr === dep.start_date) return 'start';
    if (dateStr === dep.end_date) return 'end';
    return 'mid';
  };

  // Unique color per cruise type
  const CRUISE_COLORS = {
    'tour-de-corse-croisiere-catamaran':       { bg: 'bg-blue-700',    bgLight: 'bg-blue-200',    text: 'text-white', textLight: 'text-blue-800',    label: 'Tour Corse' },
    'croisiere-catamaran-corse-du-sud':        { bg: 'bg-emerald-700', bgLight: 'bg-emerald-200', text: 'text-white', textLight: 'text-emerald-800', label: 'Corse Sud' },
    'croisiere-catamaran-ouest-corse':         { bg: 'bg-amber-600',   bgLight: 'bg-amber-200',   text: 'text-white', textLight: 'text-amber-800',   label: 'Ouest Corse' },
    'croisiere-catamaran-sardaigne-corse':     { bg: 'bg-rose-700',    bgLight: 'bg-rose-200',    text: 'text-white', textLight: 'text-rose-800',    label: 'Sardaigne' },
    'croisiere-catamaran-grece-authentique':   { bg: 'bg-cyan-700',    bgLight: 'bg-cyan-200',    text: 'text-white', textLight: 'text-cyan-800',    label: 'Grece' },
    'croisiere-catamaran-grenadines-caraibes': { bg: 'bg-violet-700',  bgLight: 'bg-violet-200',  text: 'text-white', textLight: 'text-violet-800',  label: 'Grenadines' },
  };
  const getCruiseColor = (slug) => CRUISE_COLORS[slug] || { bg: 'bg-gray-700', bgLight: 'bg-gray-200', text: 'text-white', textLight: 'text-gray-800', label: slug?.split('-').slice(-2).join(' ') || '' };

  const prevMonth = () => { if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); } else setSelectedMonth(m => m - 1); };
  const nextMonth = () => { if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); } else setSelectedMonth(m => m + 1); };

  const selectDep = (dep) => {
    setSelectedDep(selectedDep?.id === dep.id ? null : dep);
    setShowAddPassenger(false);
    setShowEditDep(false);
  };

  const handleDeleteDep = async (depId) => {
    if (!window.confirm('Supprimer ce depart et tous ses passagers ?')) return;
    try {
      await axios.delete(`${API}/cruise-dates/${depId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Depart supprime');
      setSelectedDep(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <AdminLayout title="Planning des departs"><div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}</div></AdminLayout>;

  return (
    <AdminLayout title="Planning des departs">
      <div className="space-y-6" data-testid="admin-planning">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
          <StatCard icon={Ship} label="Departs a venir" value={stats.totalDepartures} color="bg-[#0A2540]" />
          <StatCard icon={Users} label="Remplissage" value={`${stats.avgOccupancy}%`} sub={`${stats.bookedCabins}/${stats.totalCabins} cab.`} color="bg-[#2A9D8F]" />
          <StatCard icon={User} label="Passagers" value={stats.totalPassengers} color="bg-[#C4724F]" />
          <StatCard icon={Euro} label="CA confirme" value={`${stats.confirmedRevenue.toLocaleString('fr-FR')} EUR`} color="bg-[#D4AF37]" />
          <StatCard icon={TrendingUp} label="CA en attente" value={`${stats.pendingRevenue.toLocaleString('fr-FR')} EUR`} color="bg-[#94A3B8]" />
        </div>

        {/* Month nav + filter + new departure button */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={prevMonth} data-testid="prev-month-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="font-display text-base sm:text-xl font-bold text-[#0A2540] min-w-[160px] sm:min-w-[200px] text-center">{MONTHS_FR[selectedMonth]} {selectedYear}</h2>
            <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={nextMonth} data-testid="next-month-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-8 text-xs shrink-0"
              onClick={() => { setShowNewDep(!showNewDep); setSelectedDep(null); }} data-testid="new-departure-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Nouveau depart
            </Button>
            <Select value={filterCruise} onValueChange={setFilterCruise}>
              <SelectTrigger className="w-full sm:w-[220px] h-8"><SelectValue placeholder="Toutes les croisieres" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les croisieres</SelectItem>
                {(data?.cruises || []).map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* New Departure Form */}
        {showNewDep && (
          <NewDepartureForm
            cruises={data?.cruises || []}
            token={token}
            onCreated={() => { setShowNewDep(false); fetchData(); }}
            onCancel={() => setShowNewDep(false)}
          />
        )}

        {/* Cruise Color Legend */}
        <div className="flex flex-wrap gap-2 px-1">
          {Object.entries(CRUISE_COLORS).map(([slug, c]) => (
            <div key={slug} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${c.bgLight} border border-current ${c.textLight}`} />
              <span className="text-[10px] sm:text-xs text-[#4A5568] font-medium">{c.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-[#E2E8F0]">
            <div className="w-3 h-3 rounded-sm bg-slate-700" />
            <span className="text-[10px] sm:text-xs text-[#4A5568] font-medium">= Client nomme</span>
          </div>
        </div>

        {/* Calendar */}
        <Card className="border-0 shadow-lg overflow-hidden" data-testid="calendar-grid">
          <div className="grid grid-cols-7 bg-[#0A2540]">
            {['L','M','M','J','V','S','D'].map((d, i) => (
              <div key={i} className="text-center py-2 sm:py-2.5 text-[10px] sm:text-xs font-semibold text-white/80">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 border-l border-t border-[#E2E8F0]">
            {calendarDays.map((day, idx) => {
              const deps = getDepsForDay(day);
              const isToday = day && new Date().getDate() === day && new Date().getMonth() === selectedMonth && new Date().getFullYear() === selectedYear;
              return (
                <div key={idx} className={`min-h-[48px] sm:min-h-[90px] border-r border-b border-[#E2E8F0] p-0.5 sm:p-1 ${!day ? 'bg-[#F8F9FA]' : 'bg-white'} ${isToday ? 'ring-2 ring-inset ring-[#D4AF37]' : ''}`}>
                  {day && (
                    <>
                      <div className={`text-[9px] sm:text-xs font-medium mb-0.5 sm:mb-1 ${isToday ? 'text-[#D4AF37] font-bold' : 'text-[#94A3B8]'}`}>{day}</div>
                      {deps.map(dep => {
                        const pos = getDayPosition(day, dep);
                        const cc = getCruiseColor(dep.cruise_slug);
                        const isStart = pos === 'start';
                        const isEnd = pos === 'end';
                        const roundedL = isStart ? 'rounded-l-md' : 'rounded-l-none';
                        const roundedR = isEnd ? 'rounded-r-md' : 'rounded-r-none';
                        const occ = dep.booked_cabins || 0;
                        const total = dep.total_cabins || 4;
                        const hasClient = !!dep.client_name;
                        const barBg = dep.status === 'cancelled' ? 'bg-gray-400 text-white line-through' : hasClient ? `${cc.bg} ${cc.text}` : `${cc.bgLight} ${cc.textLight}`;
                        const displayName = dep.client_name || cc.label;
                        return (
                          <button key={dep.id} onClick={() => selectDep(dep)}
                            className={`w-full text-left px-1 py-0.5 sm:py-1 mb-0.5 ${roundedL} ${roundedR} text-[7px] sm:text-[10px] font-bold truncate cursor-pointer transition-all hover:opacity-80 ${barBg} ${selectedDep?.id === dep.id ? 'ring-2 ring-[#D4AF37] ring-offset-1' : ''} ${dep.is_public === false ? 'border-l-2 border-l-[#D4AF37]' : ''}`}>
                            {isStart && (
                              <>
                                {dep.is_public === false && <Lock className="inline h-2 w-2 mr-0.5 opacity-80" />}
                                <span>{displayName}</span>
                                <span className="ml-0.5 opacity-70 text-[7px]">{occ}/{total}</span>
                              </>
                            )}
                            {!isStart && (
                              <span className="opacity-80">{displayName}</span>
                            )}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Selected Departure Detail */}
        {selectedDep && (
          <Card className={`shadow-lg ${selectedDep.is_public === false ? 'border-2 border-amber-400' : 'border-2 border-[#D4AF37]'}`} data-testid="departure-detail">
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-base sm:text-lg font-bold text-[#0A2540] truncate">
                      {selectedDep.client_name && <span className="text-[#D4AF37]">{selectedDep.client_name} - </span>}
                      {data?.cruises?.find(c => c.slug === selectedDep.cruise_slug)?.name || selectedDep.cruise_slug}
                    </h3>
                    {selectedDep.is_public === false ? (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Lock className="h-2.5 w-2.5 mr-0.5" />Prive (admin seul)</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Eye className="h-2.5 w-2.5 mr-0.5" />Public</Badge>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-[#4A5568]">
                    {formatDate(selectedDep.start_date)} - {formatDate(selectedDep.end_date)} | {selectedDep.catamaran}
                    {selectedDep.departure_time && <span className="ml-2 text-[#D4AF37] font-medium">Embarq. {selectedDep.departure_time}</span>}
                    {selectedDep.arrival_time && <span className="ml-2 text-[#C4724F] font-medium">Debarq. {selectedDep.arrival_time}</span>}
                  </p>
                  {selectedDep.notes && <p className="text-xs text-amber-600 mt-1 italic">Note: {selectedDep.notes}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShowEditDep(!showEditDep); setShowAddPassenger(false); }} data-testid="edit-departure-btn">
                    <Edit className="h-3.5 w-3.5 mr-1" />Modifier
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteDep(selectedDep.id)} data-testid="delete-departure-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedDep(null); setShowAddPassenger(false); setShowEditDep(false); }}><X className="h-4 w-4" /></Button>
                </div>
              </div>

              {/* Edit form */}
              {showEditDep && (
                <EditDepartureForm
                  dep={selectedDep}
                  cruises={data?.cruises || []}
                  token={token}
                  onSaved={() => { setShowEditDep(false); fetchData(); }}
                  onCancel={() => setShowEditDep(false)}
                />
              )}

              {/* Occupancy bar */}
              {!showEditDep && (
                <>
                  <div>
                    <div className="flex justify-between text-xs text-[#4A5568] mb-1">
                      <span>{selectedDep.booked_cabins || 0} / {selectedDep.total_cabins || 4} cabines</span>
                      <span>{selectedDep.passengers?.length || 0} passager(s) inscrits</span>
                    </div>
                    <div className="w-full h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#2A9D8F] to-[#D4AF37] transition-all"
                        style={{ width: `${Math.min(selectedDep.occupancy_rate || 0, 100)}%` }} />
                    </div>
                  </div>

                  {/* Revenue */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MiniStat label="Prix cabine" value={`${selectedDep.cabin_price?.toLocaleString('fr-FR')} EUR`} icon={Euro} />
                    <MiniStat label="Privatisation" value={`${selectedDep.privatization_price?.toLocaleString('fr-FR')} EUR`} icon={Euro} />
                    <MiniStat label="CA confirme" value={`${(selectedDep.confirmed_revenue || 0).toLocaleString('fr-FR')} EUR`} icon={CheckCircle2} color="text-[#2A9D8F]" />
                    <MiniStat label="CA en attente" value={`${(selectedDep.pending_revenue || 0).toLocaleString('fr-FR')} EUR`} icon={Clock} color="text-[#D4AF37]" />
                  </div>
                </>
              )}

              {/* Passengers Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Passagers ({selectedDep.passengers?.length || 0})
                  </h4>
                  <Button size="sm" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white h-7 text-xs"
                    onClick={() => { setShowAddPassenger(!showAddPassenger); setShowEditDep(false); }} data-testid="add-passenger-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" />Ajouter un passager
                  </Button>
                </div>

                {showAddPassenger && (
                  <AddPassengerForm depId={selectedDep.id} cabinPrice={selectedDep.cabin_price || 0} token={token}
                    onAdded={() => { fetchData(); setShowAddPassenger(false); }}
                    onCancel={() => setShowAddPassenger(false)} />
                )}

                {(selectedDep.passengers?.length || 0) > 0 ? (
                  <div className="space-y-2 mt-2">
                    {selectedDep.passengers.map(p => (
                      <PassengerRow key={p.id} p={p} depId={selectedDep.id} token={token} onRefresh={fetchData} />
                    ))}
                  </div>
                ) : !showAddPassenger && (
                  <div className="text-center py-6 text-sm text-[#94A3B8] bg-[#F8F9FA] rounded-lg">
                    <User className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    Aucun passager inscrit. Cliquez "Ajouter un passager" pour commencer.
                  </div>
                )}
              </div>

              {/* Bookings from online */}
              {selectedDep.bookings?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-[#0A2540] mb-2">Reservations en ligne ({selectedDep.bookings.length})</h4>
                  <div className="space-y-1">
                    {selectedDep.bookings.map((b, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#F8F9FA] rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-[#4A5568]" />
                          <span className="font-medium text-[#0A2540]">{b.customer_name}</span>
                          <span className="text-xs text-[#4A5568]">{b.cabin_count} cab.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{b.total_amount?.toLocaleString('fr-FR')} EUR</span>
                          <Badge className={b.booking_status === 'confirmed' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-yellow-100 text-yellow-700 text-[10px]'}>
                            {b.booking_status === 'confirmed' ? 'Confirmee' : 'En attente'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pre-Boarding Section */}
              <PreBoardingSection
                departure={selectedDep}
                token={token}
                cruiseName={data?.cruises?.find(c => c.slug === selectedDep.cruise_slug)?.name || selectedDep.cruise_slug}
              />
            </CardContent>
          </Card>
        )}

        {/* Month list */}
        <div>
          <h3 className="font-display text-lg font-bold text-[#0A2540] mb-3">{MONTHS_FR[selectedMonth]} {selectedYear} ({monthDepartures.length} depart{monthDepartures.length > 1 ? 's' : ''})</h3>
          {monthDepartures.length > 0 ? (
            <div className="space-y-2">
              {monthDepartures.map(dep => {
                const cc = getCruiseColor(dep.cruise_slug);
                return (
                <Card key={dep.id} className={`cursor-pointer transition-all hover:shadow-md ${selectedDep?.id === dep.id ? 'ring-2 ring-[#0A2540]' : ''} ${dep.is_public === false ? 'border-dashed border-amber-300' : ''}`}
                  onClick={() => selectDep(dep)} data-testid={`list-dep-${dep.id}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className={`w-2 h-10 sm:h-12 rounded-full shrink-0 ${cc.bg}`} />
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0A2540] text-xs sm:text-sm truncate flex items-center gap-1.5">
                            {dep.client_name && <span className="text-[#D4AF37] font-bold">{dep.client_name} - </span>}
                            {data?.cruises?.find(c => c.slug === dep.cruise_slug)?.name || dep.cruise_slug}
                            {dep.is_public === false && <Lock className="h-3 w-3 text-amber-500 shrink-0" />}
                          </div>
                          <div className="text-[10px] sm:text-xs text-[#4A5568] flex items-center gap-2 sm:gap-3 flex-wrap">
                            <span>{formatDate(dep.start_date)} - {formatDate(dep.end_date)}</span>
                            <span className="hidden sm:inline">{dep.catamaran}</span>
                            {(dep.passengers?.length || 0) > 0 && <span className="text-[#C4724F] font-medium">{dep.passengers.length} pass.</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 shrink-0 text-sm pl-5 sm:pl-0">
                        <div className="text-right hidden sm:block">
                          <div className="font-bold text-[#0A2540]">{dep.cabin_price?.toLocaleString('fr-FR')} EUR/cab</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-base sm:text-lg text-[#0A2540]">{dep.booked_cabins || 0}/{dep.total_cabins || 4}</div>
                          <div className="text-[10px] text-[#4A5568]">cabines</div>
                        </div>
                        <Badge className={`${dep.status === 'full' ? 'bg-red-700 text-white' : dep.status === 'few_spots' ? 'bg-amber-600 text-white' : dep.status === 'cancelled' ? 'bg-gray-400 text-white' : dep.client_name ? `${cc.bg} ${cc.text}` : `${cc.bgLight} ${cc.textLight}`} text-xs`}>{dep.occupancy_rate || 0}%</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-[#94A3B8]">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
              Aucun depart pour {MONTHS_FR[selectedMonth]} {selectedYear}
            </CardContent></Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const formatDate = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-4"><div className="flex items-center gap-3">
    <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center shrink-0`}><Icon className="h-5 w-5 text-white" /></div>
    <div className="min-w-0"><p className="text-xs text-[#4A5568]">{label}</p><p className="font-bold text-[#0A2540] text-sm truncate">{value}</p>
    {sub && <p className="text-[10px] text-[#94A3B8] truncate">{sub}</p>}</div>
  </div></CardContent></Card>
);

const MiniStat = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#F8F9FA] rounded-lg p-3">
    <div className="flex items-center gap-1.5 mb-1"><Icon className={`h-3.5 w-3.5 ${color || 'text-[#4A5568]'}`} /><span className="text-[10px] text-[#4A5568]">{label}</span></div>
    <p className="font-bold text-sm text-[#0A2540]">{value}</p>
  </div>
);

export default AdminPlanning;
